import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search, Users as UsersIcon, Download, X, Mail,
  Building2, UserPlus, Truck, UserCheck, CheckCircle,
  AlertCircle, RefreshCw, Trash2
} from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { authAPI } from '../../lib/api';
import type { UserProfile } from '../../lib/api';
import { toast } from 'sonner';
import { Tabs, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { useFormatters } from '../../contexts/PreferencesContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Alert, AlertDescription } from '../../components/ui/alert';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { useAuth } from '../../contexts/AuthContext';
import { UserDetailsModal } from './UserDetailsModal';
import {
  ChangeRoleDialog,
  ActivityLogDialog,
  BulkApproveDialog,
  BulkSendInvitationsDialog,
} from './UserManagementDialogs';
import UserTable from './UserTable';
import SearchableSelect from '../../components/ui/SearchableSelect';
import { useUserActions } from './useUserActions';
import { EditUserProfileDialog } from './EditUserProfileDialog';
import { SetTempPasswordDialog } from './SetTempPasswordDialog';
import { TempPasswordModal } from './TempPasswordModal';
import type { SortField, SortDirection } from './UserTable';

type StatusFilter = 'all' | 'active' | 'inactive';
type ApprovalFilter = 'all' | 'approved' | 'pending';
/** Invitation lifecycle stage. Mirrors InvitationBadge in UserTable exactly:
 *  joined → has_joined; sent → invitation_sent but not yet joined; not_invited → neither. */
type InvitationFilter = 'all' | 'joined' | 'sent' | 'not_invited';

export default function UsersManagement() {
  const navigate = useNavigate();
  const { user, startImpersonation } = useAuth();
  const { formatDate } = useFormatters();
  const actions = useUserActions();

  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  // CR-5312: default to Active so admins triaging the list don't see
  // deactivated users by default. The "All" / "Inactive" options stay
  // available for cleanup workflows.
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('active');
  const [approvalFilter, setApprovalFilter] = useState<ApprovalFilter>('all');
  const [companyFilter, setCompanyFilter] = useState<string>('all');
  const [roleFilter, setRoleFilter] = useState<string>('all');
  const [invitationFilter, setInvitationFilter] = useState<InvitationFilter>('all');

  // Sorting & Pagination
  const [sortField, setSortField] = useState<SortField>('created_at');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;

  // Selection & Tabs
  const [selectedUsers, setSelectedUsers] = useState<Set<number>>(new Set());
  const [userTypeTab, setUserTypeTab] = useState<'supplier' | 'customer'>('supplier');

  // CR-5312: confirm-delete dialog state.
  const [deleteCandidate, setDeleteCandidate] = useState<UserProfile | null>(null);

  // Permissions
  /** Only Django superusers may grant/revoke target users' is_superuser/is_staff (modal switch). */
  const isSuperAdmin = Boolean(user?.is_superuser);
  // `can_manage_users` is a top-level flag on the serialized user (derived from
  // profile.role.can_manage_users). Reading it off user.role (a numeric id)
  // always returned undefined, which redirected Supplier Admins to /dashboard.
  const canManageUsers = user?.is_staff || user?.is_superuser ||
    user?.platform === 'HELIX' ||
    Boolean(user?.can_manage_users);
  // "Set temp password" is a supplier-admin (operating-org) tool — and Helix /
  // super, who can administer anyone. Customer admins must not see it.
  const canSetTempPassword = Boolean(
    user?.is_superuser ||
    user?.platform === 'HELIX' ||
    user?.company_type === 'supplier'
  );
  // Login As is a supplier-admin / Helix / super tool (same audience as the old
  // sidebar entry). The backend still enforces who may actually be impersonated.
  const canLoginAs = canSetTempPassword;

  // Impersonate a user straight from the list. Mirrors LoginAsPage: hit the
  // impersonate endpoint, then hand the tokens to AuthContext which persists
  // them and triggers a full reload into the target user's session.
  const handleLoginAs = async (userProfile: UserProfile) => {
    try {
      const response = await authAPI.impersonate(userProfile.user.id);
      const data = response.data.data;
      startImpersonation(
        data.target_user,
        data.target_token,
        data.original_token,
        undefined,
        data.audit_log_id,
      );
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { error?: string } } };
      toast.error(axiosErr.response?.data?.error || 'Failed to login as this user');
    }
  };

  useEffect(() => {
    if (!canManageUsers) { navigate('/dashboard'); return; }
    actions.fetchUsers();
  }, [canManageUsers, navigate]);

  useEffect(() => { setCurrentPage(1); }, [searchTerm, statusFilter, approvalFilter, companyFilter, roleFilter, invitationFilter]);

  // --- Computed Values ---

  const { users } = actions;
  const supplierCount = useMemo(() => users.filter(u => u.company_type !== 'customer').length, [users]);
  const customerCount = useMemo(() => users.filter(u => u.company_type === 'customer').length, [users]);
  const pendingInSelectionCount = useMemo(() => {
    return users.filter((u) => selectedUsers.has(u.id) && !u.is_approved).length;
  }, [users, selectedUsers]);
  const uniqueCompanies = useMemo(() => Array.from(new Set(users.map(u => u.company_name))).sort(), [users]);
  const uniqueRoles = useMemo(() => Array.from(new Set(users.map(u => u.role_name).filter(Boolean))).sort(), [users]);

  const filteredAndSortedUsers = useMemo(() => {
    let filtered = users.filter(u =>
      userTypeTab === 'customer' ? u.company_type === 'customer' : u.company_type !== 'customer'
    );
    if (searchTerm.trim()) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(u =>
        u.user.full_name.toLowerCase().includes(search) ||
        u.user.email.toLowerCase().includes(search) ||
        u.company_name.toLowerCase().includes(search) ||
        u.job_title?.toLowerCase().includes(search) ||
        u.department?.toLowerCase().includes(search)
      );
    }
    if (statusFilter !== 'all') filtered = filtered.filter(u => u.user.is_active === (statusFilter === 'active'));
    if (approvalFilter !== 'all') filtered = filtered.filter(u => u.is_approved === (approvalFilter === 'approved'));
    if (companyFilter && companyFilter !== 'all') {
      filtered = filtered.filter(u => u.company_name === companyFilter);
    }
    if (roleFilter !== 'all') filtered = filtered.filter(u => u.role_name === roleFilter);
    if (invitationFilter !== 'all') {
      filtered = filtered.filter(u => {
        if (invitationFilter === 'joined') return u.user.has_joined;
        if (invitationFilter === 'sent') return !u.user.has_joined && Boolean(u.user.invitation_sent);
        return !u.user.has_joined && !u.user.invitation_sent;
      });
    }

    filtered.sort((a, b) => {
      if (sortField === 'last_login') {
        const aLogin = a.user.last_login ? new Date(a.user.last_login).getTime() : 0;
        const bLogin = b.user.last_login ? new Date(b.user.last_login).getTime() : 0;
        if (aLogin < bLogin) return sortDirection === 'asc' ? -1 : 1;
        if (aLogin > bLogin) return sortDirection === 'asc' ? 1 : -1;
        return 0;
      }
      let aVal: string | Date;
      let bVal: string | Date;
      switch (sortField) {
        case 'name': aVal = a.user.full_name; bVal = b.user.full_name; break;
        case 'email': aVal = a.user.email; bVal = b.user.email; break;
        case 'company': aVal = a.company_name; bVal = b.company_name; break;
        case 'role': aVal = a.role_name; bVal = b.role_name; break;
        case 'created_at': aVal = new Date(a.created_at); bVal = new Date(b.created_at); break;
        default: return 0;
      }
      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
    return filtered;
  }, [users, userTypeTab, searchTerm, statusFilter, approvalFilter, companyFilter, roleFilter, invitationFilter, sortField, sortDirection]);

  const totalPages = Math.ceil(filteredAndSortedUsers.length / itemsPerPage);
  const paginatedUsers = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return filteredAndSortedUsers.slice(start, start + itemsPerPage);
  }, [filteredAndSortedUsers, currentPage, itemsPerPage]);

  const hasActiveFilters = searchTerm || statusFilter !== 'all' || approvalFilter !== 'all' ||
    companyFilter !== 'all' || roleFilter !== 'all' || invitationFilter !== 'all';

  // Stats
  const joinedCount = useMemo(() => filteredAndSortedUsers.filter(u => u.user.has_joined).length, [filteredAndSortedUsers]);
  const notJoinedCount = filteredAndSortedUsers.length - joinedCount;
  const invitedSentCount = useMemo(() =>
    filteredAndSortedUsers.filter(u => !u.user.has_joined && u.user.invitation_sent).length,
    [filteredAndSortedUsers]);
  const notInvitedCount = useMemo(() =>
    filteredAndSortedUsers.filter(u => !u.user.has_joined && !u.user.invitation_sent).length,
    [filteredAndSortedUsers]);
  const uniqueCompanyCount = useMemo(() => new Set(filteredAndSortedUsers.map(u => u.company_name)).size, [filteredAndSortedUsers]);
  const joinedPercent = filteredAndSortedUsers.length > 0
    ? Math.round((joinedCount / filteredAndSortedUsers.length) * 100) : 0;

  // --- Handlers ---

  const isSelf = (userProfile: { user: { id: number } }) => userProfile.user.id === user?.id;

  const handleSort = (field: SortField) => {
    if (sortField === field) setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDirection('asc'); }
  };

  const toggleUserSelection = (userId: number) => {
    const next = new Set(selectedUsers);
    if (next.has(userId)) next.delete(userId); else next.add(userId);
    setSelectedUsers(next);
  };

  const toggleSelectAll = () => {
    const pageIds = paginatedUsers.map((u) => u.id);
    const allPageSelected = pageIds.length > 0 && pageIds.every((id) => selectedUsers.has(id));
    const next = new Set(selectedUsers);
    if (allPageSelected) {
      pageIds.forEach((id) => next.delete(id));
    } else {
      pageIds.forEach((id) => next.add(id));
    }
    setSelectedUsers(next);
  };

  const selectAllNotInvited = () => {
    const ids = filteredAndSortedUsers
      .filter(u => !u.user.has_joined && !u.user.invitation_sent).map(u => u.id);
    setSelectedUsers(new Set(ids));
  };

  const clearFilters = () => {
    setSearchTerm(''); setStatusFilter('all'); setApprovalFilter('all');
    setCompanyFilter('all'); setRoleFilter('all'); setInvitationFilter('all');
  };

  const exportToCSV = () => {
    const headers = ['Name', 'Email', 'Company', 'Role', 'Status', 'Approved', 'Last Login', 'Created At'];
    const rows = filteredAndSortedUsers.map(u => [
      u.user.full_name, u.user.email, u.company_name, u.role_name,
      u.user.is_active ? 'Active' : 'Inactive', u.is_approved ? 'Yes' : 'No',
      u.user.last_login ? new Date(u.user.last_login).toISOString() : '',
      formatDate(u.created_at)
    ]);
    const csv = [headers.join(','), ...rows.map(r => r.map(c => `"${c}"`).join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `users-export-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const handleBulkApproveWrapped = async () => {
    await actions.handleBulkApprove(Array.from(selectedUsers));
    setSelectedUsers(new Set());
  };

  const handleApproveUserWrapped = async (userId: number) => {
    await actions.handleApproveUser(userId);
  };

  const handleBulkSendInvitationsWrapped = async () => {
    await actions.handleBulkSendInvitations(Array.from(selectedUsers));
    setSelectedUsers(new Set());
  };

  if (!canManageUsers) return null;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
          <UsersIcon className="h-8 w-8" />Users Management
        </h1>
        <div className="flex gap-2">
          <Button onClick={actions.fetchUsers} variant="outline" size="icon" title="Refresh">
            <RefreshCw className="h-4 w-4" />
          </Button>
          <Button onClick={exportToCSV} variant="outline" className="gap-2">
            <Download className="h-4 w-4" />Export CSV
          </Button>
        </div>
      </div>

      {actions.error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" /><AlertDescription>{actions.error}</AlertDescription>
        </Alert>
      )}

      {/* Stats Row */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card><CardContent className="pt-6"><div className="flex items-center gap-3">
          <div className="p-2 bg-blue-100 rounded-lg"><UsersIcon className="h-5 w-5 text-blue-600" /></div>
          <div><p className="text-2xl font-bold">{filteredAndSortedUsers.length}</p>
          <p className="text-sm text-muted-foreground">Total Users from {uniqueCompanyCount} companies</p></div>
        </div></CardContent></Card>
        <Card><CardContent className="pt-6"><div className="flex items-center gap-3">
          <div className="p-2 bg-green-100 rounded-lg"><UserCheck className="h-5 w-5 text-green-600" /></div>
          <div><p className="text-2xl font-bold text-green-600">{joinedCount}</p>
          <p className="text-sm text-muted-foreground">Users Joined ({joinedPercent}% of total)</p></div>
        </div></CardContent></Card>
        <Card><CardContent className="pt-6"><div className="flex items-center gap-3">
          <div className="p-2 bg-amber-100 rounded-lg"><Mail className="h-5 w-5 text-amber-600" /></div>
          <div><p className="text-2xl font-bold text-amber-600">{notJoinedCount}</p>
          <p className="text-sm text-muted-foreground">Not Joined ({invitedSentCount} invitations sent)</p></div>
        </div></CardContent></Card>
      </div>

      {/* Primary Supplier / Customer tabs — the main split; filters + Add
          below belong to the active tab. */}
      <Tabs value={userTypeTab} onValueChange={(v) => { setUserTypeTab(v as 'supplier' | 'customer'); setCurrentPage(1); }}>
        <TabsList className="grid w-full max-w-md grid-cols-2 h-11">
          <TabsTrigger value="supplier" className="gap-2 text-base">
            <Truck className="h-4 w-4" />Supplier Users<Badge variant="secondary" className="ml-1">{supplierCount}</Badge>
          </TabsTrigger>
          <TabsTrigger value="customer" className="gap-2 text-base">
            <Building2 className="h-4 w-4" />Customer Users<Badge variant="secondary" className="ml-1">{customerCount}</Badge>
          </TabsTrigger>
        </TabsList>
      </Tabs>

      {/* Action Bar (scoped to the active tab) */}
      <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-3">
        <div className="flex flex-wrap gap-2">
          <Button onClick={() => navigate(`/users/invite?type=${userTypeTab}`)} className="gap-2">
            <UserPlus className="h-4 w-4" />Add {userTypeTab === 'customer' ? 'Customer' : 'Supplier'} User
          </Button>
          {selectedUsers.size > 0 && (
            <>
              <Button
                variant="outline"
                onClick={() => actions.setShowBulkApproveDialog(true)}
                disabled={actions.actionLoading || pendingInSelectionCount === 0}
                className="gap-2"
                title={pendingInSelectionCount === 0 ? 'No pending users in selection' : undefined}
              >
                <CheckCircle className="h-4 w-4" />
                Approve pending ({pendingInSelectionCount})
              </Button>
              <Button
                variant="outline"
                onClick={() => actions.setShowBulkInviteDialog(true)}
                disabled={actions.actionLoading}
                className="gap-2"
              >
                <Mail className="h-4 w-4" />
                Send invitations ({selectedUsers.size})
              </Button>
              <Button variant="outline" onClick={() => setSelectedUsers(new Set())}>Clear Selection</Button>
            </>
          )}
          {notInvitedCount > 0 && (
            <Button variant="outline" onClick={selectAllNotInvited} className="gap-2">
              <Mail className="h-4 w-4" />Select All Not Invited ({notInvitedCount})
            </Button>
          )}
        </div>
        <div className="flex gap-2 w-full md:w-auto">
          <div className="relative flex-1 md:w-[260px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input placeholder="Search users..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-10" />
          </div>
          <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v as StatusFilter)}>
            <SelectTrigger className="w-[140px]"><SelectValue placeholder="All Members" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Members</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="inactive">Inactive</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Additional Filters */}
      <div className="flex flex-wrap gap-2">
        <Select value={approvalFilter} onValueChange={(v) => setApprovalFilter(v as ApprovalFilter)}>
          <SelectTrigger className="w-[150px]"><SelectValue placeholder="Approval" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Approvals</SelectItem>
            <SelectItem value="approved">Approved</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
          </SelectContent>
        </Select>
        <SearchableSelect
          className="w-[220px]"
          placeholder="All Companies"
          value={companyFilter}
          onChange={setCompanyFilter}
          options={[
            { value: 'all', label: 'All Companies' },
            ...uniqueCompanies.map((c) => ({ value: c, label: c })),
          ]}
        />
        <Select value={roleFilter} onValueChange={setRoleFilter}>
          <SelectTrigger className="w-[180px]"><SelectValue placeholder="Role" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Roles</SelectItem>
            {uniqueRoles.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={invitationFilter} onValueChange={(v) => setInvitationFilter(v as InvitationFilter)}>
          <SelectTrigger className="w-[180px]"><SelectValue placeholder="Invitation" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Invitations</SelectItem>
            <SelectItem value="joined">Joined</SelectItem>
            <SelectItem value="sent">Invitation Sent</SelectItem>
            <SelectItem value="not_invited">Not Invited</SelectItem>
          </SelectContent>
        </Select>
        {hasActiveFilters && (
          <Button variant="ghost" size="sm" onClick={clearFilters}><X className="h-4 w-4 mr-1" />Clear Filters</Button>
        )}
        <span className="text-sm text-muted-foreground self-center ml-2">
          Showing {paginatedUsers.length} of {filteredAndSortedUsers.length} users
        </span>
      </div>


      {/* Table */}
      <UserTable
        users={paginatedUsers} selectedUsers={selectedUsers}
        onToggleSelection={toggleUserSelection} onToggleSelectAll={toggleSelectAll}
        onViewDetails={actions.openUserDetails} onToggleActive={actions.handleToggleActive}
        onChangeRole={actions.openChangeRoleDialog} onSendInvitation={actions.handleSendInvitation}
        onApprove={handleApproveUserWrapped}
        onSafeDelete={(u) => setDeleteCandidate(u)}
        onViewActivity={actions.handleViewActivityLog}
        onLoginAs={handleLoginAs} canLoginAs={canLoginAs}
        isSelf={isSelf} loading={actions.loading} actionLoading={actions.actionLoading}
        sortField={sortField} sortDirection={sortDirection} onSort={handleSort}
        currentPage={currentPage} totalPages={totalPages}
        totalFiltered={filteredAndSortedUsers.length} onPageChange={setCurrentPage}
      />

      {/* CR-5312: confirm before delete/deactivate. Backend decides
          which one based on the user's login + audit footprint, but
          surface the *intent* to the admin so they understand why
          inactive users can't be hard-deleted. */}
      <Dialog open={!!deleteCandidate} onOpenChange={(open) => { if (!open) setDeleteCandidate(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <Trash2 className="h-5 w-5" />
              Delete or deactivate user
            </DialogTitle>
            <DialogDescription>
              {deleteCandidate ? (
                <>
                  <span className="font-medium">{deleteCandidate.user.full_name || deleteCandidate.user.email}</span>
                  {' '}will be{' '}
                  <span className="font-medium">permanently deleted</span>{' '}
                  if they have never logged in and have no audit history.
                  Otherwise the account will be{' '}
                  <span className="font-medium">deactivated</span>{' '}
                  so the audit trail stays intact — you can re-enable later from the Inactive filter.
                </>
              ) : null}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDeleteCandidate(null)} disabled={actions.actionLoading}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={actions.actionLoading}
              onClick={async () => {
                if (!deleteCandidate) return;
                const target = deleteCandidate;
                setDeleteCandidate(null);
                await actions.handleSafeDelete(target);
              }}
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Confirm
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialogs */}
      <UserDetailsModal
        open={actions.showUserDetails} onOpenChange={actions.setShowUserDetails}
        selectedUser={actions.selectedUser} userDetailTab={actions.userDetailTab}
        setUserDetailTab={actions.setUserDetailTab}
        userSitePermissions={actions.userSitePermissions}
        savingPermission={actions.savingPermission}
        isSuperAdmin={isSuperAdmin || false} actionLoading={actions.actionLoading}
        isSelf={isSelf} handleToggleActive={actions.handleToggleActive}
        openChangeRoleDialog={actions.openChangeRoleDialog}
        handleToggleAdmin={actions.handleToggleAdmin}
        handleToggleNotification={actions.handleToggleNotification}
        handleToggleSiteLink={actions.handleToggleSiteLink}
        handleToggleSitePermission={actions.handleToggleSitePermission}
        handleApproveUser={handleApproveUserWrapped}
        handleRejectUser={actions.handleRejectUser}
        handleSendInvitation={actions.handleSendInvitation}
        openEditProfileDialog={actions.openEditProfileDialog}
        handleAdminResetPassword={actions.handleAdminResetPassword}
        openSetTempPasswordDialog={actions.openSetTempPasswordDialog}
        canSetTempPassword={canSetTempPassword}
      />
      <SetTempPasswordDialog
        open={actions.showSetTempPasswordDialog}
        onOpenChange={actions.setShowSetTempPasswordDialog}
        targetUser={actions.tempPasswordTarget}
        actionLoading={actions.actionLoading}
        onConfirm={async (forceChange) => {
          if (actions.tempPasswordTarget) {
            await actions.handleAdminSetTempPassword(actions.tempPasswordTarget, forceChange);
          }
        }}
      />
      <TempPasswordModal
        open={Boolean(actions.tempPasswordResult)}
        onOpenChange={(openVal) => { if (!openVal) actions.setTempPasswordResult(null); }}
        result={actions.tempPasswordResult}
      />
      <EditUserProfileDialog
        open={actions.showEditProfileDialog}
        onOpenChange={actions.setShowEditProfileDialog}
        selectedUser={actions.selectedUser}
        availableRoles={actions.availableRoles}
        actionLoading={actions.actionLoading}
        onSave={actions.handleUpdateProfile}
      />
      <BulkApproveDialog
        open={actions.showBulkApproveDialog} onOpenChange={actions.setShowBulkApproveDialog}
        selectedCount={selectedUsers.size}
        pendingInSelectionCount={pendingInSelectionCount}
        actionLoading={actions.actionLoading}
        onBulkApprove={handleBulkApproveWrapped}
      />
      <BulkSendInvitationsDialog
        open={actions.showBulkInviteDialog}
        onOpenChange={actions.setShowBulkInviteDialog}
        selectedCount={selectedUsers.size}
        actionLoading={actions.actionLoading}
        onConfirm={handleBulkSendInvitationsWrapped}
      />
      <ChangeRoleDialog
        open={actions.showChangeRoleDialog} onOpenChange={actions.setShowChangeRoleDialog}
        selectedUser={actions.selectedUser} availableRoles={actions.availableRoles}
        selectedRoleId={actions.selectedRoleId} setSelectedRoleId={actions.setSelectedRoleId}
        actionLoading={actions.actionLoading} onChangeRole={actions.handleChangeRole}
      />
      <ActivityLogDialog
        open={actions.showActivityLogDialog} onOpenChange={actions.setShowActivityLogDialog}
        selectedUser={actions.selectedUser} activityLogs={actions.activityLogs}
      />
    </div>
  );
}
